import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/base/Button';
import Reveal from './Reveal';
import WorksSlider from './WorksSlider';

const dramaVideos = [
  { id: 'cgHO0FaVtZQ', url: 'https://www.youtube.com/shorts/cgHO0FaVtZQ', title: '豊中市介護保険事業者連絡会 Episode01' },
  { id: '59qXWFObY1A', url: 'https://www.youtube.com/shorts/59qXWFObY1A', title: '豊中市介護保険事業者連絡会 Episode02' },
  { id: 'BpOliAhTIRY', url: 'https://www.youtube.com/shorts/BpOliAhTIRY', title: '豊中市介護保険事業者連絡会 Episode03' },
  { id: 'GmP0zXXmU8c', url: 'https://www.youtube.com/shorts/GmP0zXXmU8c', title: '豊中市介護保険事業者連絡会 Episode04' },
  { id: 'AHna89OQQ-c', url: 'https://www.youtube.com/shorts/AHna89OQQ-c', title: '豊中市介護保険事業者連絡会 Episode05' },
  { id: '6pEryIzBi8I', url: 'https://www.youtube.com/shorts/6pEryIzBi8I', title: '豊中市介護保険事業者連絡会 Episode06' },
];

type PublicTab = '縦型ドラマ' | 'プロジェクションマッピング';

interface DramaVideo {
  id: string;
  url: string;
  title: string;
}

function PublicTabSection({ dramaVideos }: { dramaVideos: DramaVideo[] }) {
  const [activeTab, setActiveTab] = useState<PublicTab>('縦型ドラマ');

  return (
    <div>
      <div className="flex justify-center mb-8">
        <div className="inline-flex bg-white/5 rounded-full p-1 flex-wrap gap-1 border border-white/10">
          {(['縦型ドラマ', 'プロジェクションマッピング'] as PublicTab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab
                  ? 'bg-teal-500 text-white'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              {tab === '縦型ドラマ' && <><i className="ri-film-line mr-1"></i>{tab}</>}
              {tab === 'プロジェクションマッピング' && <><i className="ri-projector-line mr-1"></i>{tab}</>}
            </button>
          ))}
        </div>
      </div>

      {activeTab === '縦型ドラマ' && (
        <div>
          <p className="text-zinc-500 text-center text-sm mb-8">豊中市介護保険事業者連絡会</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {dramaVideos.map((video) => (
              <div
                key={video.id}
                className="bg-white/[0.03] rounded-lg overflow-hidden hover:border-teal-400/40 transition-all border border-white/10 cursor-pointer group"
                onClick={() => window.open(video.url, '_blank')}
              >
                <div className="relative w-full bg-white/5 overflow-hidden" style={{ paddingBottom: '177.78%' }}>
                  <img
                    src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`}
                    alt={video.title}
                    className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                    <div className="w-10 h-10 flex items-center justify-center bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                      <i className="ri-play-fill text-teal-500 text-xl"></i>
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <p className="text-xs font-medium text-zinc-200 leading-snug">{video.title}</p>
                  <p className="text-xs text-teal-300 mt-1 flex items-center gap-1">
                    <i className="ri-youtube-fill"></i>Shorts
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'プロジェクションマッピング' && (
        <div>
          <p className="text-zinc-500 text-center text-sm mb-8">日本糖尿病学会 年次学術集会「岡山城 マッピング」</p>
          <div className="flex justify-center">
            <div className="w-full max-w-3xl rounded-xl overflow-hidden bg-black border border-white/10" style={{ aspectRatio: '16/9' }}>
              <iframe
                src="https://www.youtube.com/embed/x7FKRZEL2LE"
                title="プロジェクションマッピング 岡山城"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WorksSection() {
  const navigate = useNavigate();

  return (
    <section id="works" className="py-24 md:py-32 bg-site-mid">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <Reveal>
          <div className="text-center mb-20">
            <p className="eyebrow text-teal-400 text-sm md:text-base mb-4">Selected Works</p>
            <h2 className="text-3xl md:text-5xl font-mincho text-white mb-6">制作実績</h2>
            <div className="w-20 h-[3px] bg-teal-400 mx-auto rounded-full"></div>
          </div>
          </Reveal>

          {/* 公開制作実績（スライダー） */}
          <div className="mb-20">
            <Reveal>
            <h3 className="text-2xl font-mincho text-white mb-10 text-center">公開制作実績</h3>
            </Reveal>
            <WorksSlider />
          </div>

          {/* 縦型ドラマ & プロジェクションマッピング タブ */}
          <div className="mb-16">
            <PublicTabSection dramaVideos={dramaVideos} />
          </div>

          {/* エピソード紹介 */}
          <div
            onClick={() => navigate('/works-secret')}
            className="bg-white/[0.03] border border-white/10 p-8 rounded-xl mb-12 cursor-pointer hover:border-teal-400/30 transition-colors"
          >
            <h3 className="text-xl font-mincho text-white mb-4 text-center">
              <i className="ri-lightbulb-line text-teal-400 mr-2"></i>
              エピソード紹介
            </h3>
            <p className="text-zinc-400 leading-relaxed text-center max-w-4xl mx-auto">
              現場での録音から編集、MA仕上げまで一貫して対応。「音で空間を変える」ことをテーマに、細部までこだわり抜いています。小さな空間からでも、大きな感動を届けたい。映像のクオリティを音で引き上げ、見る人の心に響く音づくりを大切にしています。
            </p>
          </div>

          {/* YouTubeチャンネル */}
          <div className="bg-white/[0.03] border border-white/10 rounded-xl p-8 text-center">
            <div className="flex items-center justify-center mb-6">
              <img
                src="/images/brand/logo.jpg"
                alt="WakuWaku Logo"
                className="w-16 h-16 rounded-full mr-4"
              />
              <div>
                <div className="flex items-center justify-center mb-2">
                  <i className="ri-youtube-fill text-red-500 text-4xl mr-3"></i>
                  <h3 className="text-2xl font-mincho text-white">WakuWakuちゃんねる</h3>
                </div>
              </div>
            </div>
            <p className="text-zinc-400 mb-6 leading-relaxed">
              個人的な興味を共有する作品を多く取り扱っています。<br />
              シンプルにWakuWakuしたいが、詰まった個人サイトです。
            </p>
            <p className="text-lg font-semibold text-teal-300 mb-6">
              👂 見て、聴いて、ぐるぐる動画を体感してください。
            </p>
            <Button
              variant="primary"
              onClick={() => window.open('https://www.youtube.com/channel/UCsKae0IY33_AFxOwLIpVf0Q', '_blank')}
            >
              <i className="ri-youtube-fill mr-2"></i>
              YouTubeチャンネルを見る
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}